import { AgentPathsConfig } from "../agent-config-options";

/**
 * Fully-resolved requirement category subdirectory names, relative to
 * the requirements root. Every property is required.
 */
export interface ResolvedRequirementCategoryDirs {
  readonly business: string;
  readonly functional: string;
  readonly nonFunctional: string;
  readonly technical: string;
  readonly architecturalDecisions: string;
  readonly security: string;
  readonly data: string;
  readonly integration: string;
  readonly operational: string;
  readonly ux: string;
  readonly multiTenancy: string;
}

/**
 * Fully-resolved agent output-path roots. Every property is required.
 *
 * This is the shape that bundle code consumes at module-eval time via
 * `DEFAULT_AGENT_PATHS`, and the shape that `resolveAgentPaths()`
 * returns when consumers supply a partial `AgentPathsConfig` override.
 */
export interface ResolvedAgentPaths {
  readonly docsRoot: string;
  readonly researchRoot: string;
  readonly profilesRoot: string;
  readonly meetingsRoot: string;
  readonly requirementsRoot: string;
  readonly researchRequirementsRoot: string;
  readonly bcmRoot: string;
  readonly peopleRoot: string;
  readonly companiesRoot: string;
  readonly softwareRoot: string;
  readonly industriesRoot: string;
  readonly requirementCategoryDirs: ResolvedRequirementCategoryDirs;
}

/**
 * Canonical default subdirectory name for each requirement category.
 * These mirror the hardcoded `functional/`, `non-functional/`, … dirs
 * that the requirements bundles emitted before category dirs became
 * configurable, so the generated requirements snapshot is unchanged
 * unless a consumer overrides an entry.
 */
export const DEFAULT_REQUIREMENT_CATEGORY_DIRS: ResolvedRequirementCategoryDirs =
  Object.freeze({
    business: "business",
    functional: "functional",
    nonFunctional: "non-functional",
    technical: "technical",
    architecturalDecisions: "architectural-decisions",
    security: "security",
    data: "data",
    integration: "integration",
    operational: "operational",
    ux: "ux",
    multiTenancy: "multi-tenancy",
  });

/**
 * Canonical default values for every agent path. These mirror the
 * hardcoded paths that bundles used before `AgentPathsConfig` existed,
 * so `DEFAULT_AGENT_PATHS.*` can be substituted into bundle rule
 * content at module-eval time without changing the generated
 * `.claude/rules/*.md` snapshot.
 *
 * Consumers override the defaults by passing an `AgentPathsConfig`
 * through `AgentConfigOptions.paths` and resolving it with
 * `resolveAgentPaths()`. Every path-aware bundle threads the resolved
 * struct through its rule / skill / sub-agent content, so an override
 * propagates into the rendered output.
 */
export const DEFAULT_AGENT_PATHS: ResolvedAgentPaths = Object.freeze({
  docsRoot: "docs/src/content/docs",
  researchRoot: "docs/research",
  profilesRoot: "docs/src/content/docs/profiles",
  meetingsRoot: "docs/src/content/docs/meetings",
  requirementsRoot: "docs/src/content/docs/requirements",
  researchRequirementsRoot: "docs/research/requirements",
  bcmRoot: "docs/src/content/docs/concepts",
  peopleRoot: "docs/src/content/docs/profiles/people",
  companiesRoot: "docs/src/content/docs/profiles/companies",
  softwareRoot: "docs/src/content/docs/profiles/software",
  industriesRoot: "docs/src/content/docs/profiles/industries",
  requirementCategoryDirs: DEFAULT_REQUIREMENT_CATEGORY_DIRS,
});

/**
 * Resolve a partial `AgentPathsConfig` into a fully-populated
 * `ResolvedAgentPaths`. Unset fields cascade from their parent root:
 *
 * - `profilesRoot`, `meetingsRoot`, `requirementsRoot`, and `bcmRoot`
 *   derive from `docsRoot` when not explicitly set.
 * - `researchRequirementsRoot` derives from `researchRoot` when not
 *   explicitly set.
 * - `peopleRoot`, `companiesRoot`, `softwareRoot`, and `industriesRoot`
 *   derive from the resolved `profilesRoot` when not explicitly set,
 *   so that overriding `docsRoot` alone (or overriding `profilesRoot`
 *   alone) propagates correctly through every dependent root.
 */
export function resolveAgentPaths(
  paths?: AgentPathsConfig,
): ResolvedAgentPaths {
  const docsRoot = paths?.docsRoot ?? DEFAULT_AGENT_PATHS.docsRoot;
  const researchRoot = paths?.researchRoot ?? DEFAULT_AGENT_PATHS.researchRoot;

  const profilesRoot = paths?.profilesRoot ?? `${docsRoot}/profiles`;
  const meetingsRoot = paths?.meetingsRoot ?? `${docsRoot}/meetings`;
  const requirementsRoot =
    paths?.requirementsRoot ?? `${docsRoot}/requirements`;
  const bcmRoot = paths?.bcmRoot ?? `${docsRoot}/concepts`;

  const researchRequirementsRoot =
    paths?.researchRequirementsRoot ?? `${researchRoot}/requirements`;

  const peopleRoot = paths?.peopleRoot ?? `${profilesRoot}/people`;
  const companiesRoot = paths?.companiesRoot ?? `${profilesRoot}/companies`;
  const softwareRoot = paths?.softwareRoot ?? `${profilesRoot}/software`;
  const industriesRoot = paths?.industriesRoot ?? `${profilesRoot}/industries`;

  const categoryOverrides = paths?.requirementCategoryDirs;
  const requirementCategoryDirs: ResolvedRequirementCategoryDirs = {
    business:
      categoryOverrides?.business ?? DEFAULT_REQUIREMENT_CATEGORY_DIRS.business,
    functional:
      categoryOverrides?.functional ??
      DEFAULT_REQUIREMENT_CATEGORY_DIRS.functional,
    nonFunctional:
      categoryOverrides?.nonFunctional ??
      DEFAULT_REQUIREMENT_CATEGORY_DIRS.nonFunctional,
    technical:
      categoryOverrides?.technical ??
      DEFAULT_REQUIREMENT_CATEGORY_DIRS.technical,
    architecturalDecisions:
      categoryOverrides?.architecturalDecisions ??
      DEFAULT_REQUIREMENT_CATEGORY_DIRS.architecturalDecisions,
    security:
      categoryOverrides?.security ?? DEFAULT_REQUIREMENT_CATEGORY_DIRS.security,
    data: categoryOverrides?.data ?? DEFAULT_REQUIREMENT_CATEGORY_DIRS.data,
    integration:
      categoryOverrides?.integration ??
      DEFAULT_REQUIREMENT_CATEGORY_DIRS.integration,
    operational:
      categoryOverrides?.operational ??
      DEFAULT_REQUIREMENT_CATEGORY_DIRS.operational,
    ux: categoryOverrides?.ux ?? DEFAULT_REQUIREMENT_CATEGORY_DIRS.ux,
    multiTenancy:
      categoryOverrides?.multiTenancy ??
      DEFAULT_REQUIREMENT_CATEGORY_DIRS.multiTenancy,
  };

  return {
    docsRoot,
    researchRoot,
    profilesRoot,
    meetingsRoot,
    requirementsRoot,
    researchRequirementsRoot,
    bcmRoot,
    peopleRoot,
    companiesRoot,
    softwareRoot,
    industriesRoot,
    requirementCategoryDirs,
  };
}
